import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { analysisCostLabel, analysisCredits, apiService, UploadSummary } from '../services/apiService';
import './Upload.css';

const API_URL = process.env.REACT_APP_API_URL || '';
// Served via /upload-options/ (proxied to the backend in production); same file as
// backend static/templates/proven_upload_template.xlsx.
const TEMPLATE_URL = `${API_URL}/upload-options/template`;

interface UploadFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  message?: string;
}

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [user, setUser] = useState<any>(null);
  
  // Get authenticated user data
  useEffect(() => {
    const userData = apiService.getStoredUser();
    setUser(userData);
    
    // Load user's credit info
    if (userData?.customer_id) {
      loadCreditInfo(userData.customer_id);
    }
  }, []);
  const [uploading, setUploading] = useState(false);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadSummary | null>(null);
  const [creditInfo, setCreditInfo] = useState<any>(null);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [loadingCredits, setLoadingCredits] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending' as const
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/geo+json': ['.geojson'],
      'application/json': ['.json'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv']
    },
    multiple: true
  });

  const loadCreditInfo = async (customerId: string) => {
    setLoadingCredits(true);
    try {
      const credits = await apiService.getCreditBalance(customerId);
      setCreditInfo(credits);
      setShowCreditWarning(credits.current_credits < analysisCredits(credits));
    } catch (error) {
      console.error('Error loading credit info:', error);
      setCreditInfo(null);
    } finally {
      setLoadingCredits(false);
    }
  };

  const uploadFiles = async () => {
    if (files.length === 0 || !user?.customer_id) return;

    setUploading(true);
    setUploadResults(null);

    try {
      // Mark all files as uploading
      setFiles(prev => prev.map(f => ({ 
        ...f, 
        status: 'uploading' as const, 
        progress: 0 
      })));

      // Upload all files at once using authenticated endpoint
      const results = await apiService.uploadGeoJSONAuthenticated(
        files.map(f => f.file),
        (progressEvent: any) => {
          const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setFiles(prev => prev.map(f => ({ 
            ...f, 
            progress 
          })));
        }
      );

      // Mark all files as successful
      setFiles(prev => prev.map(f => ({ 
        ...f, 
        status: 'success' as const, 
        progress: 100,
        message: 'Upload successful'
      })));

      setUploadResults(results);

      // Automatically run analysis after successful upload
      setRunningAnalysis(true);
      try {
        console.log('Starting automatic analysis...');
        
        // Validate credits before running analysis
        const creditValidation = await apiService.validateCredits(user.customer_id);
        if (!creditValidation.valid) {
          throw new Error(creditValidation.message || 'Insufficient credits for analysis');
        }
        
        await apiService.runAnalysisAuthenticated();
        console.log('Analysis completed successfully, redirecting...');
        
        // Redirect to analysis page after successful analysis
        setTimeout(() => {
          console.log('Navigating to analysis page...');
          navigate('/analysis');
        }, 2000);
        
      } catch (analysisError) {
        console.error('Analysis failed:', analysisError);
        setRunningAnalysis(false);
        // Even if analysis fails, show upload results
        // User can manually run analysis from the Analysis page
        const errorMessage = analysisError instanceof Error ? analysisError.message : 'Unknown error';
        alert(`Upload successful, but analysis failed: ${errorMessage}. Please go to Analysis page and run analysis manually.`);
      } finally {
        // Ensure analysis state is reset after timeout
        setTimeout(() => {
          setRunningAnalysis(false);
        }, 3000);
      }

    } catch (error) {
      // Mark all files as error
      setFiles(prev => prev.map(f => ({ 
        ...f, 
        status: 'error' as const, 
        message: error instanceof Error ? error.message : 'Upload failed'
      })));
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
  };

  return (
    <div className="upload-page">
      <h1>Upload GeoJSON Files</h1>
      <p className="page-description">Upload rubber plantation GeoJSON files for Proven compliance analysis.</p>
      
      {/* Welcome message for new signups */}
      {new URLSearchParams(window.location.search).get('new_signup') && (
        <div className="welcome-message">
          <h3>Welcome to Proven!</h3>
          <p>Your account has been created successfully with free welcome credits included. You can now upload your GeoJSON files and start analyzing your rubber plantation data for EUDR compliance.</p>
        </div>
      )}
      
      <div className="upload-container">
        {/* User Information Section */}
        <div className="user-section">
          <h3>Account Information</h3>
          <div className="user-info">
            <div className="user-details">
              <div className="user-item">
                <span>Email:</span>
                <span className="user-value">{user?.email || 'Loading...'}</span>
              </div>
              <div className="user-item">
                <span>Company:</span>
                <span className="user-value">{user?.company_name || 'Loading...'}</span>
              </div>
              <div className="user-item">
                <span>Customer ID:</span>
                <span className="user-value">{user?.customer_id || 'Loading...'}</span>
              </div>
            </div>
            {loadingCredits && (
              <div className="loading-message">Loading credit information...</div>
            )}
          </div>
          
          {/* Credit Information */}
          {creditInfo && (
            <div className="credit-info">
              <h4>Credit Balance</h4>
              <div className="credit-details">
                <div className="credit-item">
                  <span>Current Credits:</span>
                  <span className="credit-value">{creditInfo.current_credits}</span>
                </div>
                <div className="credit-item">
                  <span>Analyses Available:</span>
                  <span className="credit-value">{creditInfo.analyses_available}</span>
                </div>
                <div className="credit-item">
                  <span>Cost per Analysis:</span>
                  <span className="credit-value">{analysisCostLabel(creditInfo)}</span>
                </div>
              </div>
              
              {showCreditWarning && (
                <div className="credit-warning">
                  <p><strong>⚠</strong> Insufficient credits for analysis. You need {analysisCredits(creditInfo)} credits minimum.</p>
                  <a
                    href={`/dashboard`}
                    className="btn purchase-btn"
                  >
                    Get More Credits
                  </a>
                  <a
                    href={`/dashboard`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn secondary"
                  >
                    View Dashboard
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* File Upload Section */}
        <div 
          {...getRootProps()} 
          className={`dropzone ${isDragActive ? 'active' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="dropzone-content">
            <div className="upload-icon">⇧</div>
            {isDragActive ? (
              <p>Drop your plot files here...</p>
            ) : (
              <div>
                <p>Drag & drop plot files here, or click to select files</p>
                <p className="file-types">
                  Supported formats: GeoJSON (.geojson, .json), Excel (.xlsx), CSV (.csv)
                </p>
              </div>
            )}
          </div>
        </div>

        {files.length > 0 && (
          <div className="file-list">
            <div className="file-list-header">
              <h3>Selected Files ({files.length})</h3>
              <div className="file-actions">
                <button 
                  onClick={uploadFiles} 
                  disabled={uploading || !user?.customer_id || files.length === 0}
                  className="btn primary"
                >
                  {uploading ? 'Uploading...' : 'Upload All'}
                </button>
                <button 
                  onClick={clearAll} 
                  disabled={uploading}
                  className="btn secondary"
                >
                  Clear All
                </button>
              </div>
            </div>

            <div className="files">
              {files.map((fileUpload, index) => (
                <div key={index} className={`file-item ${fileUpload.status}`}>
                  <div className="file-info">
                    <div className="file-name">{fileUpload.file.name}</div>
                    <div className="file-size">
                      {(fileUpload.file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>

                  <div className="file-status">
                    {fileUpload.status === 'uploading' && (
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${fileUpload.progress}%` }}
                        ></div>
                        <span className="progress-text">{fileUpload.progress}%</span>
                      </div>
                    )}

                    {fileUpload.status === 'success' && (
                      <div className="status-message success">✓ {fileUpload.message}</div>
                    )}

                    {fileUpload.status === 'error' && (
                      <div className="status-message error">✗ {fileUpload.message}</div>
                    )}

                    {fileUpload.status === 'pending' && (
                      <div className="status-message pending">• Ready to upload</div>
                    )}
                  </div>

                  <button 
                    onClick={() => removeFile(index)}
                    disabled={fileUpload.status === 'uploading'}
                    className="remove-btn"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Results */}
        {uploadResults && (
          <div className="upload-results">
            <h3>Upload Results</h3>
            <div className="results-grid">
              <div className="result-item">
                <span className="result-label">Total Plots:</span>
                <span className="result-value">{uploadResults.total_plots}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Total Area:</span>
                <span className="result-value">{uploadResults.total_area_hectares.toFixed(4)} hectares</span>
              </div>
              <div className="result-item">
                <span className="result-label">Unique Producers:</span>
                <span className="result-value">{uploadResults.unique_producers}</span>
              </div>
            </div>
            {!!uploadResults.duplicate_rows_merged && (
              <p className="upload-note" role="status">
                {uploadResults.rows_uploaded?.toLocaleString()} rows uploaded:{' '}
                {uploadResults.duplicate_rows_merged.toLocaleString()} repeated rows (same plot code and polygon, e.g. one row
                per harvest date) were merged, so each plot is analysed once. Their differing values, such as harvest
                dates, are kept together on the plot.
              </p>
            )}
            {!!uploadResults.codes_with_different_geometries?.length && (
              <p className="upload-note warning" role="status">
                ⚠ {uploadResults.codes_with_different_geometries.length} plot code
                {uploadResults.codes_with_different_geometries.length === 1 ? ' is' : 's are'} used for more than one
                polygon and will be checked as separate plots:{' '}
                {uploadResults.codes_with_different_geometries.slice(0, 10).join(', ')}
                {uploadResults.codes_with_different_geometries.length > 10 && ' …'}
              </p>
            )}
            
            {/* Analysis Status */}
            {runningAnalysis && (
              <div className="analysis-status">
                <div className="analysis-progress">
                  <div className="spinner"></div>
                  <span>Running overlap analysis...</span>
                </div>
                <p>Analyzing overlaps with forest reserves, protected areas, and forest cover data.</p>
              </div>
            )}
            
            {!runningAnalysis && (
              <div className="next-steps">
                <p>✓ Files uploaded and analyzed successfully!</p>
                <p>→ Redirecting to analysis results...</p>
              </div>
            )}
          </div>
        )}

        {/* Information Section */}
        <div className="info-section">
          <h3>File Requirements</h3>
          <ul>
            <li>
              <strong>Accepted formats:</strong> GeoJSON/JSON (.geojson, .json), Excel (.xlsx) or CSV (.csv).
              One plot per feature (GeoJSON) or per row (Excel/CSV).
            </li>
            <li>
              <strong>Required:</strong> a polygon boundary for each plot, in WGS84 longitude/latitude (EPSG:4326).
              In GeoJSON this is the feature geometry (Polygon or MultiPolygon); in Excel/CSV it is a column
              named <code>Geometry</code> holding WKT, e.g. <code>POLYGON((100.46 6.72, 100.47 6.72, ...))</code>.
            </li>
            <li>
              <strong>Recommended:</strong> a plot code for every plot, used in all report tables and maps
              (e.g. <code>ProductionPlace</code>, <code>siteNumber</code> or <code>Plantation Code</code>).
            </li>
            <li>
              <strong>Optional:</strong> producer name (defaults to your company name), country, and area in
              hectares (calculated from the polygon if missing). Field names are not case-sensitive, and any
              extra columns or properties are kept.
            </li>
            <li>You must be logged in to upload files.</li>
          </ul>
          <p className="template-download">
            <a href={TEMPLATE_URL} download="proven_upload_template.xlsx">
              Download Excel template
            </a>{' '}
            (example rows plus an Instructions sheet explaining every column)
          </p>
        </div>
      </div>
    </div>
  );
};

export default Upload;
